import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { Alert, IconButton } from '@mui/material'
import { useContext, useMemo, useState } from 'react'
import AppContext from '../../../../context/appContext'
import {
  useCreateExclusionAreaObservation,
  useExclusionAreaObservations,
  useResolveExclusionAreaObservation,
  useUnresolvedObservationsCount,
} from '../../../../hooks/data/useExclusionAreaObservations'
import {
  useCreateExclusionAreas,
  useDeleteExclusionAreas,
  useUpdateExclusionAreas,
} from '../../../../hooks/data/useExclusionAreas'
import { useUserRole } from '../../../../hooks/useUserRole'
import { isWriteOutcomeUnknown } from '../../../../services/ruutsApi/errors'
import { ExclusionAreaGeoJSON, RefData } from '../../../../types/apiModels'
import { useCustomSnackbarError } from '../../../../utils/Snackbar/useCustomSnackbarError'
import { MonitoringAccordion } from '../../MonitoringAccordion'
import { getFeatureId } from '../../utils'
import { useExclusionAreaContext } from '../ExclusionAreaContext'
import { useExclusionAreaWriteError } from '../useExclusionAreaWriteError'
import { ExclusionAreaObservationsModal } from '../observations/ExclusionAreaObservationsModal'
import { ExclusionAreaEditItem } from './ExclusionAreaEditItem'
import { ExclusionAreaItem } from './ExclusionAreaItem'
import { validateExclusionArea } from '../validationUtils'

interface ExclusionAreasAccordionProps {
  exclusionAreaTypes: RefData[] | undefined
  isLoading: boolean
  canEdit: boolean
  isInEditMode: boolean
  isDrawingMode: boolean
}

export const ExclusionAreasAccordion = ({
  exclusionAreaTypes,
  isLoading,
  canEdit,
  isInEditMode,
  isDrawingMode,
}: ExclusionAreasAccordionProps) => {
  const {
    exclusionAreas,
    setExclusionAreas,
    setSelectedExclusionAreaIds,
    editingItemId,
    selectedArea,
    startEditingGeometry,
    updateSelectedArea,
    cancelEdit,
    finishEdit,
    isExclusionVisible,
    setExclusionVisible,
  } = useExclusionAreaContext()
  const { currentFarm, setLoadingModalConfig, setPartialChanges, setConfirmationModalConfig } =
    useContext(AppContext)
  const { handleError } = useCustomSnackbarError()
  const { handleWriteError, blockedPeriodDialog } = useExclusionAreaWriteError()

  const createExclusionAreas = useCreateExclusionAreas({ farmId: currentFarm?.id })
  const updateExclusionAreas = useUpdateExclusionAreas({ farmId: currentFarm?.id })
  const deleteExclusionAreas = useDeleteExclusionAreas({ farmId: currentFarm?.id })

  const [accordionExpanded, setAccordionExpanded] = useState(true)
  const [selectedObservationAreaId, setSelectedObservationAreaId] = useState<string | null>(null)
  const [observationsModalOpen, setObservationsModalOpen] = useState(false)

  const { isAuditor, isGISTechnician, isFieldTechnician } = useUserRole()

  const { data: unresolvedCounts } = useUnresolvedObservationsCount({ farmId: currentFarm?.id })
  const unresolvedCountMap = useMemo(
    () =>
      Object.fromEntries(
        unresolvedCounts?.map(({ exclusionAreaId, count }) => [exclusionAreaId, count] as const) ??
          [],
      ),
    [unresolvedCounts],
  )

  const observationScope = {
    exclusionAreaId: selectedObservationAreaId ?? undefined,
    farmId: currentFarm?.id,
  }
  const { data: observations } = useExclusionAreaObservations(observationScope)
  const createObservation = useCreateExclusionAreaObservation(observationScope)
  const resolveObservation = useResolveExclusionAreaObservation(observationScope)

  const handleOpenObservations = (exclusionAreaId: string) => {
    setSelectedObservationAreaId(exclusionAreaId)
    setObservationsModalOpen(true)
  }

  const handleCloseObservations = () => {
    setObservationsModalOpen(false)
    setSelectedObservationAreaId(null)
  }

  const handleToggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation()
    setExclusionVisible(prev => !prev)
  }

  const handleEdit = (exclusionArea: ExclusionAreaGeoJSON) => {
    startEditingGeometry(getFeatureId(exclusionArea)!, exclusionArea)
  }

  const handleCancel = () => {
    cancelEdit()
    setPartialChanges(false)
  }

  const handleSave = async (updatedArea: ExclusionAreaGeoJSON) => {
    const validationError = validateExclusionArea(updatedArea, exclusionAreas || [])

    if (validationError) {
      handleError(validationError)
      return
    }

    try {
      setLoadingModalConfig({
        open: true,
        title: updatedArea.properties.id
          ? 'Actualizando área de exclusión...'
          : 'Creando área de exclusión...',
        dialogMessage: updatedArea.properties.id
          ? 'Actualizando área de exclusión...'
          : 'Creando área de exclusión...',
      })

      let savedId: string | undefined
      if (updatedArea.properties.id) {
        await updateExclusionAreas.mutateAsync({
          features: [updatedArea],
          exclusionTypes: exclusionAreaTypes || [],
        })
      } else {
        const newArea = structuredClone(updatedArea)
        delete newArea.properties.tmpId
        const [created] = await createExclusionAreas.mutateAsync({
          features: [newArea],
          exclusionTypes: exclusionAreaTypes || [],
        })
        savedId = created?.id
      }

      // mutateAsync resolves after the onSuccess invalidation refetch, so the cache is
      // already server-authoritative here; re-applying the client-side feature would
      // overwrite server-recomputed fields.
      finishEdit(savedId)
      setPartialChanges(false)
    } catch (error) {
      handleWriteError(error)
      if (isWriteOutcomeUnknown(error)) {
        // The write may have landed, so the draft must not linger looking saved; the refetch
        // the hook already started brings back server truth.
        handleCancel()
      }
    } finally {
      setLoadingModalConfig({
        open: false,
      })
    }
  }

  const handleDelete = async (exclusionArea: ExclusionAreaGeoJSON) => {
    try {
      if (!exclusionArea.properties.id && exclusionArea.properties.tmpId) {
        setExclusionAreas(prev =>
          prev.filter(area => getFeatureId(area) !== getFeatureId(exclusionArea)),
        )
        setPartialChanges(false)
        return
      }

      setConfirmationModalConfig({
        open: true,
        title: 'Eliminando área de exclusión...',
        message: '¿Está seguro de querer eliminar esta área de exclusión?',
        confirmAction: async () => {
          // The confirmAction runs detached from the surrounding try/catch, and the
          // mutation suppresses the global snackbar, so it must report its own failures.
          try {
            // mutateAsync resolves after the onSuccess invalidation refetch, so the
            // cache already excludes the deleted area; no client-side filter needed.
            await deleteExclusionAreas.mutateAsync({
              entities: [{ id: exclusionArea.properties.id }],
            })

            setPartialChanges(false)
          } catch (error) {
            handleWriteError(error)
          }
        },
      })
    } catch (error) {
      handleError(error)
    } finally {
      setLoadingModalConfig({
        open: false,
      })
    }
  }

  return (
    <>
      <MonitoringAccordion
        enableSelection
        expanded={isExclusionVisible && accordionExpanded}
        getItemId={getFeatureId}
        isLoading={isLoading}
        items={exclusionAreas || []}
        onChange={(_, expanded) => setAccordionExpanded(expanded)}
        onItemsChange={items =>
          setSelectedExclusionAreaIds(
            items.filter(item => item.properties.selected).map(getFeatureId),
          )
        }
      >
        <MonitoringAccordion.Title enableSelection>Áreas manuales</MonitoringAccordion.Title>
        <MonitoringAccordion.Description>
          Creadas aquí o importadas del KML.
        </MonitoringAccordion.Description>
        <MonitoringAccordion.Description sx={{ flexGrow: 1 }} textAlign="right">
          <IconButton component="span" disabled={isLoading} onClick={handleToggleVisibility}>
            {isExclusionVisible ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </MonitoringAccordion.Description>
        {exclusionAreas?.map(exclusionArea => {
          const areaId = getFeatureId(exclusionArea)
          return (
            <MonitoringAccordion.Item key={areaId}>
              {editingItemId === areaId ? (
                <ExclusionAreaEditItem
                  exclusionArea={selectedArea || exclusionArea}
                  exclusionAreaTypes={exclusionAreaTypes}
                  onCancel={handleCancel}
                  onChange={updateSelectedArea}
                  onSave={handleSave}
                />
              ) : (
                <>
                  <MonitoringAccordion.Item.Check itemId={areaId} />
                  <ExclusionAreaItem
                    exclusionArea={exclusionArea}
                    exclusionAreaTypes={exclusionAreaTypes}
                    isDisabled={isInEditMode || isDrawingMode || !canEdit}
                    unresolvedCount={unresolvedCountMap[exclusionArea.properties.id ?? ''] ?? 0}
                    onComment={
                      exclusionArea.properties.id
                        ? () => handleOpenObservations(exclusionArea.properties.id)
                        : undefined
                    }
                    onDelete={() => handleDelete(exclusionArea)}
                    onEdit={() => handleEdit(exclusionArea)}
                  />
                </>
              )}
            </MonitoringAccordion.Item>
          )
        })}
        {(!exclusionAreas || exclusionAreas.length === 0) && (
          <MonitoringAccordion.Item>
            <Alert severity="info" sx={{ width: '100%' }}>
              No se encontraron áreas de exclusión.
            </Alert>
          </MonitoringAccordion.Item>
        )}
      </MonitoringAccordion>

      {observationsModalOpen && selectedObservationAreaId && (
        <ExclusionAreaObservationsModal
          canComment={isAuditor || isGISTechnician || isFieldTechnician}
          canCreate={isAuditor}
          canResolve={isAuditor}
          observations={observations ?? []}
          open={observationsModalOpen}
          onClose={handleCloseObservations}
          onCreateObservation={createObservation.mutateAsync}
          onResolve={resolveObservation.mutate}
        />
      )}

      {blockedPeriodDialog}
    </>
  )
}
